import React, { lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import AuthRoutes from "../auth/routes/auth.routes";
import { useSelector } from "react-redux";
import PublicRoute from "./PublicRoute";
import PrivateRoute from "./PrivateRoute";
import ClientRoutes from "../client/routes/client.routes";
import AdminRoutes from "../admin/routes/admin.routes";
import { useRoutes } from "react-router-dom";
import MainRoutes from "../main/routes/main.routes";
import LanguageSync from "../../components/LanguageSync";
import LanguageRouter from "../../components/LanguageRouter";

// Lazy load ALL admin components to prevent them from being bundled in main chunk
const AdminDashboard = lazy(() => import("../../Admin/AdminDashboard"));
const UserManagement = lazy(() => import("../../Admin/AdminUserManagement"));
const BusinessManagement = lazy(() => import("../../Admin/AdminBusinessManagement"));
const AllUsersPage = lazy(() => import("../../Admin/UserManagement/AllUsers"));
const LocalProfessionalsPage2 = lazy(() => import("../../Admin/UserManagement/LocalProfessionalsPage"));
const InternationalProfessionalsPage2 = lazy(() => import("../../Admin/UserManagement/InternationalProfessionalsPage"));
const VerificationRequestsPage = lazy(() => import("../../Admin/UserManagement/VerificationRequestsPage"));
const BusinessDirectory = lazy(() => import("../../Admin/BUSINESS MANAGEMENT/BusinessDirectory"));
const ServiceCategories = lazy(() => import("../../Admin/BUSINESS MANAGEMENT/ServiceCategories"));
const EmergencyServices = lazy(() => import("../../Admin/BUSINESS MANAGEMENT/EmergencyServices"));
const ServiceAreas = lazy(() => import("../../Admin/BUSINESS MANAGEMENT/ServiceAreas"));
const AllOrdersPage = lazy(() => import("../../Admin/ORDER MANAGEMENT/AllOrders"));
const PendingOrdersPage = lazy(() => import("../../Admin/ORDER MANAGEMENT/PendingOrders"));
const EmergencyRequestsPage = lazy(() => import("../../Admin/ORDER MANAGEMENT/EmergencyRequests"));
const QuotesPage = lazy(() => import("../../Admin/ORDER MANAGEMENT/QuotesPage"));
const TransactionsPage = lazy(() => import("../../Admin/FINANCIAL MANAGEMENT/Transactions"));
const RevenuePage = lazy(() => import("../../Admin/FINANCIAL MANAGEMENT/Revenue"));
const PayoutsPage = lazy(() => import("../../Admin/FINANCIAL MANAGEMENT/Payouts"));
const InvoicesPage = lazy(() => import("../../Admin/FINANCIAL MANAGEMENT/invoices"));
const ReviewsAndRatings = lazy(() => import("../../Admin/Content Management/Rating"));
const SupportTickets2 = lazy(() => import("../../Admin/Content Management/SupportTickets"));
const HelpCenter22 = lazy(() => import("../../Admin/Content Management/help"));
const Announcements = lazy(() => import("../../Admin/Content Management/Announcements"));
const BusinessAnalytics = lazy(() => import("../../Admin/ANALYTICS/BusinessAnalytics"));
const UserAnalytics = lazy(() => import("../../Admin/ANALYTICS/UserAnalytics"));
const GrowthMetrics = lazy(() => import("../../Admin/ANALYTICS/GrowthMetricsDashboard"));
const GeneralSettings = lazy(() => import("../../Admin/General Settings/GeneralSettings"));
const SecuritySettings = lazy(() => import("../../Admin/General Settings/Security"));
const SystemLogs = lazy(() => import("../../Admin/General Settings/SystemLogs"));
const SystemLogs2 = lazy(() => import("../../Admin/General Settings/SystemLogs"));
const WhatsAppIntegration = lazy(() => import("../../Admin/WhatsApp Management/WhatsApp"));
const ChatManagement = lazy(() => import("../../Admin/WhatsApp Management/ChatManagement"));
const CustomNotifications = lazy(() => import("../../Admin/WhatsApp Management/CustomNotifications"));
const ProfessionalForms = lazy(() => import("../../Admin/FORM MANAGEMENT/ProfessionalForms"));
const QuoteForms = lazy(() => import("../../Admin/FORM MANAGEMENT/Quote Forms"));
const QuotePricing = lazy(() => import("../../Admin/SUBSCRIPTION & PRICING/QuotePricing"));
const SubscriptionPlansadmin = lazy(() => import("../../Admin/SUBSCRIPTION & PRICING/SubscriptionPlansManagement"));
const RegionalPricing = lazy(() => import("../../Admin/SUBSCRIPTION & PRICING/RegionalPricingManagement"));
const PerformanceMetrics = lazy(() => import("../../Admin/PROFESSIONAL SCORING/PerformanceMetrics"));
const Rankings = lazy(() => import("../../Admin/PROFESSIONAL SCORING/Rankings"));
const Rankingsadmin = lazy(() => import("../../Admin/PROFESSIONAL SCORING/Rankings"));
const ScoringSettings = lazy(() => import("../../Admin/PROFESSIONAL SCORING/ScoringSettings"));
const PaymentMilestones = lazy(() => import("../../Admin/MILESTONE MANAGEMENT/Milestones"));
const CommissionSettings = lazy(() => import("../../Admin/MILESTONE MANAGEMENT/CommissionSettings"));
const DepositRules = lazy(() => import("../../Admin/MILESTONE MANAGEMENT/DepositRules"));
const TermsConditions = lazy(() => import("../../Admin/CONTENT EDITOR/TermsEditor"));
const NewsManagement = lazy(() => import("../../Admin/CONTENT EDITOR/NewsManagement"));
const MarketingPopups = lazy(() => import("../../Admin/CONTENT EDITOR/MarketingPopups"));
const RegionSettings = lazy(() => import("../../Admin/INTERNATIONAL/RegionSettings"));
const LanguageManager = lazy(() => import("../../Admin/INTERNATIONAL/LanguageManager"));
const CurrencySettings = lazy(() => import("../../Admin/INTERNATIONAL/CurrencySettings"));
const CategoryForms = lazy(() => import("../../Admin/FORM MANAGEMENT/CategoryForms"));
const ChatManagement2 = lazy(() => import("../../Admin/chat/ChatManagement"));
const AdminClientsDashboard = lazy(() => import("../../Admin/CONTENT EDITOR/profiles"));
const Favorites2 = lazy(() => import("../../pages/Favorites"));
const DynamicPage = lazy(() => import("../../pages/DynamicPage"));
const PageList = lazy(() => import("../../pages/PageList"));
import AppBackRoute from "./app.route"; // Route config, not a component
import ProfessionalRoutes from "../professional/routes/professional.rotues"; // Route config, not a component
const ClientProfile = lazy(() => import("../client/components/ClientProfile1"));
const MyProfile = lazy(() => import("../professional/components/MyProfile"));
const GlobalNotifications = lazy(() => import("../../components/GlobalNotifications"));
const WebSocketNotificationListener = lazy(() => import("../../components/WebSocketNotificationListener"));
const DisputeRequestsPage = lazy(() => import("../../Admin/ORDER MANAGEMENT/DisputeRequestsPage"));
import { RoleEnum } from "../shared";
const ClientInvoice = lazy(() => import("../../pages/ClientInvoice"));
const ProfessionalInvoice = lazy(() => import("../../pages/ProfessionalInvoice"));
const TrustpilotSettings = lazy(() => import("../../Admin/SETTINGS/TrustpilotSettings"));
const LocationManagement = lazy(() => import("../../Admin/FORM MANAGEMENT/LocationManagement"));
const ProfessionalMonitoring = lazy(() => import("../../Admin/PROFESSIONAL SCORING/ProfessionalMonitoring"));
import { SUPPORTED_LANGUAGES } from "../../utils/languageRouting";

const RoutesConfig = () => {
    // Generate language-prefixed auth routes
    const authRoutes = [
        { path: "/auth/*", element: <AuthRoutes /> },
    ];
    
    // Add language-prefixed versions of auth routes
    const languagePrefixedAuthRoutes: any[] = [];
    SUPPORTED_LANGUAGES.filter(lang => lang !== "en").forEach(lang => {
        languagePrefixedAuthRoutes.push({
            path: `/${lang}/auth/*`,
            element: <AuthRoutes />
        });
    });

    const routes = [
        { path: "*", element: <Navigate to="/" replace /> },
        {
            element: <PublicRoute />,
            children: [
                ...authRoutes,
                ...languagePrefixedAuthRoutes,
            ],
        },
        {
            element: <PrivateRoute />,
            children: [
                ...ClientRoutes,
                ...ProfessionalRoutes,
                ...AdminRoutes,
                { path: "/admin/dashboard", element: <AdminDashboard /> },
                { path: "/admin/usermanagement", element: <UserManagement /> },
                { path: "/admin/businessmanagement", element: <BusinessManagement /> },
                { path: "/admin/usermanagement/alluser", element: <AllUsersPage /> },
                { path: "/admin/usermanagement/localprofesionals", element: <LocalProfessionalsPage2 /> },
                { path: "/admin/usermanagement/internationalprofesionals", element: <InternationalProfessionalsPage2 /> },
                { path: "/admin/usermanagement/verificationrequests", element: <VerificationRequestsPage /> },
                { path: "/admin/businesses", element: <BusinessDirectory /> },
                { path: "/admin/categories", element: <ServiceCategories /> },
                { path: "/admin/emergency-services", element: <EmergencyServices /> },
                { path: "/admin/service-areas", element: <ServiceAreas /> },
                { path: "/admin/orders", element: <AllOrdersPage /> },
                { path: "/admin/orders/pending", element: <PendingOrdersPage /> },
                { path: "/admin/orders/emergency", element: <EmergencyRequestsPage /> },
                { path: "/admin/quotes", element: <QuotesPage /> },
                { path: "/admin/transactions", element: <TransactionsPage /> },
                { path: "/admin/revenue", element: <RevenuePage /> },
                { path: "/admin/payouts", element: <PayoutsPage /> },
                { path: "/admin/invoices", element: <InvoicesPage /> },
                { path: "/admin/reviews", element: <ReviewsAndRatings /> },
                { path: "/admin/support", element: <SupportTickets2 /> },
                { path: "/admin/help-center", element: <HelpCenter22 /> },
                { path: "/admin/announcements", element: <Announcements /> },
                { path: "/admin/analytics/business", element: <BusinessAnalytics /> },
                { path: "/admin/analytics/users", element: <UserAnalytics /> },
                { path: "/admin/analytics/growth", element: <GrowthMetrics /> },
                { path: "/admin/settings", element: <GeneralSettings /> },
                { path: "/admin/security", element: <SecuritySettings /> },
                { path: "/admin/logs", element: <SystemLogs2 /> },
                { path: "/admin/trustpilot", element: <TrustpilotSettings /> },
                { path: "/admin/tools/whatsapp", element: <WhatsAppIntegration /> },
                { path: "/admin/tools/chat", element: <ChatManagement /> },
                { path: "/admin/tools/notifications", element: <CustomNotifications /> },
                { path: "/admin/forms/categories", element: <CategoryForms /> },
                { path: "/admin/forms/professionals", element: <ProfessionalForms /> },
                { path: "/admin/forms/quotes", element: <QuoteForms /> },
                { path: "/admin/pricing/quotes", element: <QuotePricing /> },
                { path: "/admin/pricing/subscriptions", element: <SubscriptionPlansadmin /> },
                { path: "/admin/pricing/regions", element: <RegionalPricing /> },
                { path: "/admin/scoring/metrics", element: <PerformanceMetrics /> },
                { path: "/admin/scoring/rankings", element: <Rankingsadmin /> },
                { path: "/admin/scoring/settings", element: <ScoringSettings /> },
                { path: "/admin/scoring/monitoring", element: <ProfessionalMonitoring /> },
                { path: "/admin/milestones/payments", element: <PaymentMilestones /> },
                { path: "/admin/milestones/commission", element: <CommissionSettings /> },
                { path: "/admin/milestones/deposits", element: <DepositRules /> },
                { path: "/admin/editor/terms", element: <TermsConditions /> },
                { path: "/admin/editor/news", element: <NewsManagement /> },
                { path: "/admin/editor/marketing", element: <MarketingPopups /> },
                { path: "/admin/international/regions", element: <RegionSettings /> },
                { path: "/admin/international/languages", element: <LanguageManager /> },
                { path: "/admin/international/currencies", element: <CurrencySettings /> },
                { path: "/admin/analytics/chat", element: <ChatManagement2 /> },
                { path: "/admin/editprofile", element: <AdminClientsDashboard /> },
                { path: "/admin/favorites", element: <Favorites2 /> }, // Only one will be used; duplicate path
                { path: "/admin/dispute-requests", element: <DisputeRequestsPage /> },
                { path: "admin/locationmanagement", element: <LocationManagement /> },
            ],
        },
        ...AppBackRoute,
        ...MainRoutes,
        { path: "/pages", element: <PageList /> },
        // Dynamic page route - must be before the catch-all route
        { path: "/:url/*", element: <DynamicPage /> },

        // // ,
        // { path: "/Terms", element: <TermsPage /> },
        // { path: "/Cookie", element: <CookiesPolicyPage /> },
        // { path: "/projects", element: <ProjectsPage /> },
        // { path: "/internationalhompage", element: <ProHomepage /> },
        // { path: "/plumberregistration", element: <PlumberRegistration /> },
        // { path: "/plumberrequestform", element: <PlumberRequestForm /> },
        // { path: "/lawyerRequestForm", element: <LawyerRequestForm /> },
        // { path: "/plumberprofile", element: <PlumberProfile /> },
        // { path: "/professionalplumberprofile", element: <ProfessionalPlumberProfile /> },
        // { path: "/international-professionals", element: <InternationalProfessional /> },
        // { path: "/jobs", element: <InternationalProfessionalsPage /> },
        // { path: "/local-professionals", element: <LocalProfessionalsPage /> },
        // { path: "/professional-international", element: <InternationalProfessionalsProfile /> },
        // { path: "/walletd", element: <WalletPage /> },
        // { path: "/myprofile", element: <MyProfile /> },
        // { path: "/international_professional_registration", element: <InternationalProfessionalRegistration /> },
        // { path: "/progress", element: <ProgressDashboard /> },

        // { path: "/myoffers", element: <OffersDashboard /> },
        // { path: "/myproposals", element: <ProposalsDashboard /> },
        // { path: "/support", element: <SupportTickets /> },
        // { path: "/wallet", element: <WalletDashboard /> },
        // { path: "/withdrawalhistroy", element: <WithdrawHistory /> },
        // { path: "/profilesettings", element: <ProfileSettings /> },
        // { path: "/changepassword", element: <ChangePassword /> },
        // { path: "/DeleteAccount", element: <DeleteAccount /> },
        // { path: "/MessageAndVideoCall", element: <MessagesAndVideoCall /> },

        // { path: "/clients/settings", element: <ClientSettings /> },
        // { path: "/clients/progress", element: <ProgressPage /> },
        // { path: "/postajob", element: <PostJobPage /> },
        // { path: "/myjobs", element: <MyJobsPage /> },
        // { path: "/clients/myoffers", element: <MyOffersPage /> },
        // { path: "/clients/MessageAndVideoCall", element: <CMessagesAndVideoCall /> },
        // { path: "/clients/Wallet", element: <ClientWalletPage /> },
        // { path: "/clients/profilesettings", element: <ClientProfileSettings /> },
        // { path: "/clients/support", element: <ClientSupportTickets /> },
        // { path: "/profile-intro", element: <ClientProfileIntro /> },
        // { path: "/menu/contact", element: <ContactPage /> },
        // { path: "/invite", element: <InviteFriendsComponent /> },
        // { path: "/categories", element: <CategoriesAndLocationComponent /> },

    ];
    const routing = useRoutes(routes);
    return routing;
};

const AppRoutes = () => {
    const { isAuthenticated, rehydrated, user } = useSelector((state: any) => state.auth);
    if (!rehydrated) {
        return null;
    }
    return (
        <BrowserRouter>
            <LanguageSync />
            <LanguageRouter>
                <RoutesConfig />
                {isAuthenticated && (Number(user?.role?.id) === RoleEnum.professional || Number(user?.role?.id) === RoleEnum.internationl_professional )&& <GlobalNotifications />}
                {isAuthenticated && <WebSocketNotificationListener />}
            </LanguageRouter>
        </BrowserRouter>
    );

};

export default AppRoutes;
